import { PANO_KUTUCUKLARI, PANO_RENKLERI, type PanoIsSatiri, type PanoKutucugu, type PanoRengi, type PanoRiser, type PanoYaniti } from "@oasis/domain";
import { cevir } from "@/lib/dil";
import { riserSuz } from "@/lib/riser-belgeleri";

/**
 * D-071: Dashboard'un saf mantığı. Sunucu riser başına durum ve tamir işi satırlarını gönderir (GET /api/pano; tipler alan
 * paketinde: pano.ts), hazır cümle göndermez. Süzme, sayma, seçim geçişleri ve ekrandaki bütün sayılı cümleler buradadır;
 * ekranlar/Pano.tsx yalnız bunları çağırır ve çizer. JSX yok.
 *
 * İKİ KÜME, İKİ AD (karıştırılırsa sayılar makul görünür ama eksik çıkar):
 *  - `batchListesi`: yalnız batch süzgecinden geçmiş riserler. Kutucuk sayıları ve notları, katmanların ALT notları ve soluk
 *    grupları, "Completed (n)" buradan kurulur.
 *  - `kutucukListesi`: batchListesi ∩ seçili kutucuğun büyük sayısının riserleri. Şeritteki sayı ve katmanın ANA satırları.
 *  Çizilen haplar `hapListesi`'dir: kutucukListesi ∩ arama. Arama kutucuk sayılarını ve katmanları değiştirmez.
 *
 * Tekil yazım (Bekir, 11.10.2026): sayı 1 iken cümle tekil yazılır ("1 riser listed"). Tekil ve çoğul ayrı sözlük anahtarıdır ve
 * ikisi de tam cümledir: parça birleştirilmez, kısa önekli kalıp açılmaz (lib/dil.ts, KALIP EŞLEME: kısa kalıp sunucudan gelen
 * başka cümleleri yarım çevirir). Aynı nedenle "{0} skipped" diye bir anahtar yoktur; metin "skipped" anahtarıyla kurulur.
 *
 * Bu dosyadaki her sınıf dizesi tam yazılır (birleştirilerek kurulmaz), yoksa derlenen stile girmez.
 */

/* ---------------------------------------------------------------- seçim ---------------------------------------------------------------- */

/** Ekranın dört seçimi. `rn`: ayrıntısı açık riser (yoksa null). */
export type PanoSecim = { batchNo: string | null; kutucuk: PanoKutucugu | null; arama: string; rn: string | null };
export const BOS_SECIM: PanoSecim = { batchNo: null, kutucuk: null, arama: "", rn: null };
/** Ekrandan çıkıp dönünce hatırlanan kısım: YALNIZ süzgeç. Seçili riser hatırlanmaz. */
export type PanoSuzgeci = Pick<PanoSecim, "batchNo" | "kutucuk" | "arama">;
/** Hatırlanan süzgeçten açılış seçimi: riser her açılışta seçili değildir. */
export const acilisSecimi = (suzgec: PanoSuzgeci): PanoSecim => ({ batchNo: suzgec.batchNo, kutucuk: suzgec.kutucuk, arama: suzgec.arama, rn: null });

/* ---------------------------------------------------------------- adlar ---------------------------------------------------------------- */

/**
 * Sekiz adım kutucuğunun adı. Adım adıdır ve ÇEVRİLMEZ: bu yüzden `cevir` içine alınmaz ("Receiving", "Disassembly", "Repair" ve
 * "Assembly" sözlükte var; sarılsaydı Türkçe kipte "Teslim alma", "Söküm", "Tamir", "Montaj" olurdu). Dokuzuncu kutucuk (Done) bir
 * adım değildir; adı çevrilir.
 */
const ADIM_ADI: Readonly<Record<Exclude<PanoKutucugu, "tamam">, string>> = {
  mrb: "MRB Intake",
  teslim: "Receiving",
  sokum: "Disassembly",
  dispo: "Disposition Report",
  rework: "Rework Scope",
  tamir: "Repair",
  finishing: "Finishing",
  montaj: "Assembly",
};
export const kutucukAdi = (k: PanoKutucugu): string => (k === "tamam" ? cevir("Done") : ADIM_ADI[k]);

/** Lejant durumunun adı. Yeşil durum Done kutucuğuyla aynı kümeyi sayar ve aynı adı taşır. */
export function renkAdi(r: PanoRengi): string {
  switch (r) {
    case "baslamadi": return cevir("Not started");
    case "kabul_muayene": return cevir("Intake & inspection");
    case "tamir_montaj": return cevir("Repair & assembly");
    case "tamam": return cevir("Done");
  }
}
/** Hap ve lejanttaki 14 px karenin dolgusu. Taralı mavi D-067'nin sınıfıdır (styles.css). */
export const RENK_KARESI: Readonly<Record<PanoRengi, string>> = {
  baslamadi: "border border-line-strong bg-surface-3",
  kabul_muayene: "bg-warn",
  tamir_montaj: "bg-info-tarali",
  tamam: "bg-ok",
};
/** Hapın ipucu: "R002 · Repair & assembly". Renk tek başına anlam taşımaz; aynı bilgi sözle de durur. */
export const hapIpucu = (r: Pick<PanoRiser, "rn" | "renk">): string => `${r.rn} · ${renkAdi(r.renk)}`;

/* ---------------------------------------------------------------- kümeler ---------------------------------------------------------------- */

/**
 * Riserin tamir gerektiren komponent sayısı (Bekir, 12.10.2026): tamir durumu "yok" olmayan hatlar (0–5; bir borusu tamir gerektiren
 * riser 1, beş borusu da gerektiren 5 sayılır) ARTI beş hat dışındaki tamir gerektiren komponentler (Seal Sub, Fastener, tanınmayan
 * adla açılmış iş; sunucu `digerKomponentler`de yalnız tamir gerektirenleri gönderir). Tamir planlaması henüz yapılmamış komponent
 * de sayılır. Bir komponentte iki iş olsa da komponent 1'dir.
 */
export const tamirKomponentSayisi = (r: Pick<PanoRiser, "hatlar" | "digerKomponentler">): number =>
  r.hatlar.reduce((n, h) => n + (h.tamir === "yok" ? 0 : 1), 0) + r.digerKomponentler.length;

/**
 * Riser bu kutucuğun büyük sayısında mı (tıklayınca listede kalan riserler; Repair'de ikinci sayı). Hiçbir kural Assembly raporuna
 * bakmaz: raporu yüklü (yeşil) riserin MRB'si eksikse, rework raporu bekleniyorsa ya da tamir gerektiren hattı kaldıysa ilgili
 * kutucukta kalır. Repair: en az bir komponenti tamir gerektiren riser (açık işi olan ya da bulgusu planlama bekleyen hat, Seal Sub,
 * Fastener ya da tanınmayan adla açılmış iş).
 * Finishing ve Assembly (Bekir, 12.10.2026) Assembly durumundan okunur ve ikisi de raporu yüklenmemiş riseri sayar: Finishing =
 * Finishing'e gelmiş ama beş hattı henüz gelmemiş riser (`kismi`; beş hattı gelmiş olup beş hat dışı bir komponentte açık işi kalan
 * riser de sunucuda bu kümededir); Assembly = beş hattı Finishing'e gelmiş, evrakı tamam ya da eksik riser (`belge_eksik` + `hazir`).
 */
export function kutucukRiseri(k: PanoKutucugu, r: PanoRiser): boolean {
  switch (k) {
    case "mrb": return !r.mrb;
    case "teslim": return r.teslim === "bekliyor";
    case "sokum": return r.sokum === "bekliyor";
    case "dispo": return r.dispo === "bekleniyor";
    case "rework": return r.rework === "bekleniyor";
    case "tamir": return tamirKomponentSayisi(r) > 0;
    case "finishing": return r.montaj === "kismi";
    case "montaj": return r.montaj === "belge_eksik" || r.montaj === "hazir";
    case "tamam": return r.asmRaporu;
  }
}

/** Yalnız batch süzgeci (`null` = All; batch'siz riser yalnız All'da): kutucuk sayılarının ve katman alt notlarının girdisi. */
export function batchListesi(riserler: readonly PanoRiser[], batchNo: string | null): PanoRiser[] {
  return riserSuz(riserler, "", batchNo);
}
/** batchListesi ∩ kutucuk; kutucuk seçili değilse batchListesi. Arama yok: şeritteki sayı ve katmanın ana satırları. */
export function kutucukListesi(riserler: readonly PanoRiser[], s: Pick<PanoSecim, "batchNo" | "kutucuk">): PanoRiser[] {
  const liste = batchListesi(riserler, s.batchNo);
  const k = s.kutucuk;
  return k === null ? liste : liste.filter((r) => kutucukRiseri(k, r));
}
/** Çizilen haplar: kutucukListesi ∩ arama (RN ve Assembly SN içinde). */
export function hapListesi(riserler: readonly PanoRiser[], s: Pick<PanoSecim, "batchNo" | "kutucuk" | "arama">): PanoRiser[] {
  return riserSuz(kutucukListesi(riserler, s), s.arama, null);
}

/* ---------------------------------------------------------------- seçim geçişleri ---------------------------------------------------------------- */

/** Batch ya da kutucuk değişince: seçili riser yeni kutucukListesi'nde yoksa ayrıntısı kapanır. Her zaman yeni nesne döner. */
function riseriKoru(s: PanoSecim, riserler: readonly PanoRiser[]): PanoSecim {
  const kalir = s.rn === null || kutucukListesi(riserler, s).some((r) => r.rn === s.rn);
  return { ...s, rn: kalir ? s.rn : null };
}

/** Batch değişir, kutucuk kalır. Aynı batch'e ikinci tıklama hiçbir şeyi değiştirmez (katman satırından seçilmiş, listede olmayan riser de kalır). */
export function batchSec(s: PanoSecim, batchNo: string | null, riserler: readonly PanoRiser[]): PanoSecim {
  return s.batchNo === batchNo ? { ...s } : riseriKoru({ ...s, batchNo }, riserler);
}
/** Seçili kutucuğa tıklama seçimi kaldırır, başka kutucuğa tıklama onu seçer (aynı anda tek kutucuk). Batch ve arama kalır. */
export function kutucukTikla(s: PanoSecim, k: PanoKutucugu, riserler: readonly PanoRiser[]): PanoSecim {
  return riseriKoru({ ...s, kutucuk: s.kutucuk === k ? null : k }, riserler);
}
/** "Clear filter": kutucuk kalkar; batch ve arama kalır. */
export function suzgeciTemizle(s: PanoSecim, riserler: readonly PanoRiser[]): PanoSecim {
  return s.kutucuk === null ? { ...s } : riseriKoru({ ...s, kutucuk: null }, riserler);
}
/** Yalnız arama değişir; seçili riser kalır (hapı görünmese de ayrıntısı açık kalır). */
export function aramaYaz(s: PanoSecim, arama: string): PanoSecim {
  return { ...s, arama };
}
/** Seçili risere ikinci tıklama ayrıntıyı kapatır, başka risere tıklama onu seçer. Batch, kutucuk ve arama kalır. */
export function riserTikla(s: PanoSecim, rn: string): PanoSecim {
  return { ...s, rn: s.rn === rn ? null : rn };
}
/**
 * Seçimi gelen veriyle doğrular: batch artık yoksa All'a döner, riser sistemde yoksa ayrıntısı kapanır. Geçerli seçim AYNI nesne
 * olarak döner (ekran bunu her çizimde çağırır; gereksiz yeni nesne üretmez).
 */
export function secimiDogrula(s: PanoSecim, yanit: Pick<PanoYaniti, "riserler" | "batchler">): PanoSecim {
  const batchVar = s.batchNo === null || yanit.batchler.some((b) => b.no === s.batchNo);
  const riserVar = s.rn === null || yanit.riserler.some((r) => r.rn === s.rn);
  return batchVar && riserVar ? s : { ...s, batchNo: batchVar ? s.batchNo : null, rn: riserVar ? s.rn : null };
}

/* ---------------------------------------------------------------- kutucuklar ---------------------------------------------------------------- */

export interface KutucukOzeti {
  kutucuk: PanoKutucugu;
  /** Büyük sayı metni: "23"; Repair'de "14 – 9" (tamir gerektiren komponent – riser). */
  buyuk: string;
  /** Büyük sayının riser sayısı = tıklayınca listede kalan riser (Repair'de ikinci sayı). */
  riser: number;
  /** Yalnız Repair: tamir gerektiren komponent sayısı ((riser, komponent) çiftleri); ötekilerde null. */
  tamirKomponent: number | null;
  /** Alt satırın payı, paydası ve yüzdesi. Repair'de üçü de null; payda 0 ise yüzde null ("—"). */
  pay: number | null;
  payda: number | null;
  yuzde: number | null;
  /** Alt satırın sözü ("MRBs received") ve beşinci satırdaki not (koşulu sağlanmıyorsa ""). */
  soz: string;
  not: string;
}

const say = (liste: readonly PanoRiser[], kosul: (r: PanoRiser) => boolean): number => liste.reduce((n, r) => n + (kosul(r) ? 1 : 0), 0);
const topla = (liste: readonly PanoRiser[], deger: (r: PanoRiser) => number): number => liste.reduce((n, r) => n + deger(r), 0);
const atlandiMi = (durum: string): boolean => durum === "atlandi" || durum === "atlandi_cikarim";
const yuzdeHesabi = (pay: number, payda: number): number | null => (payda > 0 ? Math.round((pay / payda) * 100) : null);

/** "4 skipped": kayıtlı ve çıkarımla atlanan birlikte; atlanan yoksa "". */
const atlananSozu = (n: number): string => (n > 0 ? `${n} ${cevir("skipped")}` : "");
/**
 * "1 MRB uploaded, not matched to a riser yet"; eşleşmemiş MRB yoksa "". Eşleşmemiş MRB'nin riseri ve batch'i bilinmediği için sayı
 * batch seçiliyken de toplamdır. MRB Intake kutucuğunun notu ve katmanının alt notu aynı cümledir.
 */
export const bagsizMrbNotu = (bagsizMrb: number): string => (bagsizMrb > 0 ? cevir("{0} MRB uploaded, not matched to a riser yet", bagsizMrb) : "");

/**
 * Dokuz kutucuğun sayıları, PANO_KUTUCUKLARI sırasıyla. Girdi batchListesi'dir (kutucukListesi DEĞİL): kutucuk seçmek ve arama
 * yazmak sayıları değiştirmez. `bagsizMrb` batch'ten bağımsız toplamdır (eşleşmemiş MRB'nin riseri ve batch'i bilinmez).
 */
export function kutucukOzetleri(batchListesi: readonly PanoRiser[], bagsizMrb: number): KutucukOzeti[] {
  const toplam = batchListesi.length;
  const oranli = (kutucuk: PanoKutucugu, pay: number, payda: number, soz: string, not: string): KutucukOzeti => {
    const riser = say(batchListesi, (r) => kutucukRiseri(kutucuk, r));
    return { kutucuk, buyuk: String(riser), riser, tamirKomponent: null, pay, payda, yuzde: yuzdeHesabi(pay, payda), soz, not };
  };
  const ozet = (kutucuk: PanoKutucugu): KutucukOzeti => {
    switch (kutucuk) {
      case "mrb":
        return oranli(kutucuk, say(batchListesi, (r) => r.mrb), toplam, cevir("MRBs received"), bagsizMrbNotu(bagsizMrb));
      case "teslim": {
        const atlanan = say(batchListesi, (r) => atlandiMi(r.teslim));
        return oranli(kutucuk, say(batchListesi, (r) => r.teslim === "alindi"), toplam - atlanan, cevir("received"), atlananSozu(atlanan));
      }
      case "sokum": {
        const atlanan = say(batchListesi, (r) => atlandiMi(r.sokum));
        return oranli(kutucuk, say(batchListesi, (r) => r.sokum === "sokuldu"), toplam - atlanan, cevir("disassembled"), atlananSozu(atlanan));
      }
      case "dispo": {
        const yuklendi = say(batchListesi, (r) => r.dispo === "yuklendi");
        const payda = yuklendi + say(batchListesi, (r) => r.dispo === "bekleniyor");
        return oranli(kutucuk, yuklendi, payda, cevir("reports uploaded"), payda === 1 ? cevir("of 1 riser that reached this step") : cevir("of {0} risers that reached this step", payda));
      }
      case "rework": {
        const geldi = say(batchListesi, (r) => r.rework === "geldi");
        const payda = geldi + say(batchListesi, (r) => r.rework === "bekleniyor");
        return oranli(kutucuk, geldi, payda, cevir("reports uploaded"), payda === 1 ? cevir("of 1 riser with a rework decision") : cevir("of {0} risers with a rework decision", payda));
      }
      case "tamir": {
        const riser = say(batchListesi, (r) => kutucukRiseri(kutucuk, r));
        const tamirKomponent = topla(batchListesi, tamirKomponentSayisi);
        const bulgu = topla(batchListesi, (r) => r.bekleyenBulgu);
        const not = bulgu === 0 ? "" : bulgu === 1 ? cevir("1 finding awaiting planning") : cevir("{0} findings awaiting planning", bulgu);
        return { kutucuk, buyuk: `${tamirKomponent} – ${riser}`, riser, tamirKomponent, pay: null, payda: null, yuzde: null, soz: cevir("components – risers"), not };
      }
      case "finishing": {
        // Pay: beş hattı Finishing'e gelmiş riser (evrakı eksik, hazır ya da raporu yüklü). Payda: pay + bu kutucuğun riserleri.
        const besHat = say(batchListesi, (r) => r.montaj === "belge_eksik" || r.montaj === "hazir" || r.montaj === "tamam");
        return oranli(kutucuk, besHat, besHat + say(batchListesi, (r) => kutucukRiseri(kutucuk, r)), cevir("with all 5 lines"), cevir("1 to 4 of 5 lines in Finishing"));
      }
      case "montaj": {
        const monte = say(batchListesi, (r) => r.asmRaporu);
        return oranli(kutucuk, monte, monte + say(batchListesi, (r) => kutucukRiseri(kutucuk, r)), cevir("assembled"), cevir("5 lines in Finishing, documents pending"));
      }
      case "tamam":
        return oranli(kutucuk, say(batchListesi, (r) => r.asmRaporu), toplam, cevir("risers"), cevir("assembly report uploaded"));
    }
  };
  return PANO_KUTUCUKLARI.map(ozet);
}

/** "73%"; payda sıfırsa "—". */
export const yuzdeMetni = (yuzde: number | null): string => (yuzde === null ? "—" : `${yuzde}%`);

/**
 * Kutucuğun ipucu: "MRB Intake — 23 · 62 / 85 MRBs received · 73%"; Repair'de "Repair — 14 – 9 · components – risers"; payda 0
 * ise yüzde yerine "—". Ad, söz ve sayılar özetten gelir; ayrı sözlük satırı gerekmez.
 */
export function kutucukIpucu(o: KutucukOzeti): string {
  const bas = `${kutucukAdi(o.kutucuk)} — ${o.buyuk}`;
  return o.pay === null || o.payda === null ? `${bas} · ${o.soz}` : `${bas} · ${o.pay} / ${o.payda} ${o.soz} · ${yuzdeMetni(o.yuzde)}`;
}

/**
 * Alt satırın sözünü ikiye böler: son sözcük yüzdeyle birlikte kırılır ("62 / 85 MRBs" ve altında "received · 73%"); yüzde tek
 * başına alt satıra düşmez. Tek sözcükse baş boştur.
 */
export function sonSozcukAyir(soz: string): [bas: string, son: string] {
  const kirpik = soz.trim();
  const i = kirpik.lastIndexOf(" ");
  return i < 0 ? ["", kirpik] : [kirpik.slice(0, i), kirpik.slice(i + 1)];
}

/**
 * Lejant: çizilen haplardaki (hapListesi) durum sayıları, PANO_RENKLERI sırasıyla. Kutucuk seçili değilken dört durum da yazar
 * (sıfır dahil); kutucuk seçiliyken sayısı sıfır olan durum çıkar.
 */
export function lejant(haplar: readonly PanoRiser[], kutucukSecili: boolean): { renk: PanoRengi; sayi: number }[] {
  return PANO_RENKLERI.map((renk) => ({ renk, sayi: say(haplar, (r) => r.renk === renk) })).filter((x) => !kutucukSecili || x.sayi > 0);
}

/** Seçici kartının sayacı: "85 risers listed" (çizilen hap sayısı). */
export const listeSayaci = (n: number): string => (n === 1 ? cevir("1 riser listed") : cevir("{0} risers listed", n));

/** Süzgeç şeridinin cümlesi; `riserSayisi` kutucukListesi'nin boyudur (aramadan bağımsız). */
export function seritMetni(batchNo: string | null, riserSayisi: number): string {
  if (batchNo === null) return riserSayisi === 1 ? cevir("selected · riser list filtered to 1 riser") : cevir("selected · riser list filtered to {0} risers", riserSayisi);
  return riserSayisi === 1 ? cevir("selected · batch {0} · riser list filtered to 1 riser", batchNo) : cevir("selected · batch {0} · riser list filtered to {1} risers", batchNo, riserSayisi);
}

/* ---------------------------------------------------------------- katmanlar ---------------------------------------------------------------- */

/** Katman başlığındaki cümle (kutucuğun büyük sayısıyla) ve sağdaki alt başlık. Başlık "{kutucuk adı} · {cümle}" diye çizilir. */
export function katmanBasligi(o: KutucukOzeti): { cumle: string; altBaslik: string } {
  const n = o.riser;
  switch (o.kutucuk) {
    case "mrb":
      return { cumle: n === 1 ? cevir("1 riser without an MRB") : cevir("{0} risers without an MRB", n), altBaslik: cevir("manufacturing record book not in OASIS") };
    case "teslim":
      return { cumle: n === 1 ? cevir("1 riser not received yet") : cevir("{0} risers not received yet", n), altBaslik: cevir("no receiving record yet") };
    case "sokum":
      return { cumle: n === 1 ? cevir("1 riser not disassembled yet") : cevir("{0} risers not disassembled yet", n), altBaslik: cevir("received or not, no disassembly record yet") };
    case "dispo":
      return { cumle: n === 1 ? cevir("1 riser waiting for the disposition report") : cevir("{0} risers waiting for the disposition report", n), altBaslik: cevir("reached this step, report not uploaded") };
    case "rework":
      return { cumle: n === 1 ? cevir("1 riser waiting for the rework report") : cevir("{0} risers waiting for the rework report", n), altBaslik: cevir("rework decided, report not uploaded") };
    case "tamir": {
      const komponent = o.tamirKomponent ?? 0;
      // Komponent sayısı riser sayısından küçük olamaz: riser 1 iken komponent 1 ya da daha çok; riser 1 değilken komponent de 1 değildir.
      const cumle = n !== 1 ? cevir("{0} components need repair on {1} risers", komponent, n) : komponent === 1 ? cevir("1 component needs repair on 1 riser") : cevir("{0} components need repair on 1 riser", komponent);
      return { cumle, altBaslik: cevir("one row per component in repair") };
    }
    case "finishing":
      return {
        cumle: n === 1 ? cevir("1 riser with 1 to 4 of 5 lines in Finishing") : cevir("{0} risers with 1 to 4 of 5 lines in Finishing", n),
        altBaslik: cevir("reached Finishing, not ready for assembly yet"),
      };
    case "montaj":
      return {
        cumle: n === 1 ? cevir("1 riser with all 5 lines in Finishing") : cevir("{0} risers with all 5 lines in Finishing", n),
        altBaslik: cevir("assembly report not uploaded yet"),
      };
    case "tamam":
      return { cumle: n === 1 ? cevir("1 riser with all work completed") : cevir("{0} risers with all work completed", n), altBaslik: cevir("assembly report uploaded") };
  }
}

/** Ana satırı olmayan katmanın cümlesi. Done bir bekleme adımı değildir: orada "waiting" denmez. */
export const katmanBosMetni = (k: PanoKutucugu): string => (k === "tamam" ? cevir("No riser in this filter.") : cevir("No riser is waiting at this step."));

/** Disassembly katmanının Status hücresi. Teslimi atlanmış riser teslim alınmış gibi yazılır: "Received, not disassembled". */
export function sokumSatirDurumu(r: Pick<PanoRiser, "teslim">): { metin: string; ton: "neutral" | "warn" } {
  return r.teslim === "bekliyor" ? { metin: cevir("Not received yet"), ton: "neutral" } : { metin: cevir("Received, not disassembled"), ton: "warn" };
}

/**
 * Disposition katmanının "Disassembly" hücresi: söküldüyse "disassembled", atlandıysa (kayıtlı ya da çıkarım) soluk "skipped".
 * Bu katmanın riserinde söküm "bekliyor" olmaz (sunucunun değişmezi); olursa hücre soluk tire kalır.
 */
export function dispoSokumHucresi(r: Pick<PanoRiser, "sokum">): { metin: string; soluk: boolean } {
  if (r.sokum === "sokuldu") return { metin: cevir("disassembled"), soluk: false };
  return atlandiMi(r.sokum) ? { metin: cevir("skipped"), soluk: true } : { metin: "—", soluk: true };
}

/** Adımı atlayan riserlerin numaraları (kayıtlı ve çıkarım birlikte), girdinin sırasıyla (sunucu RN sırasıyla gönderir). Girdi batchListesi. */
export function atlananlar(batchListesi: readonly PanoRiser[], adim: "teslim" | "sokum"): string[] {
  return batchListesi.filter((r) => atlandiMi(r[adim])).map((r) => r.rn);
}
/** "4 risers skipped this step: R034, R108, R109, R135"; atlanan yoksa "". Girdi batchListesi. */
export function atlananNotu(batchListesi: readonly PanoRiser[], adim: "teslim" | "sokum"): string {
  const rnler = atlananlar(batchListesi, adim);
  if (!rnler.length) return "";
  return rnler.length === 1 ? cevir("1 riser skipped this step: {0}", rnler[0]) : cevir("{0} risers skipped this step: {1}", rnler.length, rnler.join(", "));
}

/** "6 risers need no rework"; sayı 0 ise "". Girdi batchListesi. */
export function reworkGerekmeyenNotu(batchListesi: readonly PanoRiser[]): string {
  const n = say(batchListesi, (r) => r.rework === "gerekmiyor");
  if (n === 0) return "";
  return n === 1 ? cevir("1 riser needs no rework") : cevir("{0} risers need no rework", n);
}

/**
 * "11 findings awaiting planning on 4 risers"; bulgu yoksa "". Girdi batchListesi. İlk sayı Repair kutucuğunun notundaki sayıyla
 * aynıdır: riserin TOPLAM bekleyen bulgusu (Seal Sub ve Fastener dahil). Bulgusu bekleyen her riser Repair listesindedir (bulgusu
 * hangi komponentte olursa). Sayı katmandaki "awaiting planning" satırlarının bulgu toplamından yine büyük olabilir: açık işi olan
 * komponentin bekleyen bulgusu iş satırında yazılmaz.
 */
export function bulguNotu(batchListesi: readonly PanoRiser[]): string {
  const bulgu = topla(batchListesi, (r) => r.bekleyenBulgu);
  if (bulgu === 0) return "";
  const riser = say(batchListesi, (r) => r.bekleyenBulgu > 0);
  // Bulgu sayısı riser sayısından küçük olamaz: riser 1 değilken bulgu da 1 değildir.
  if (riser !== 1) return cevir("{0} findings awaiting planning on {1} risers", bulgu, riser);
  return bulgu === 1 ? cevir("1 finding awaiting planning on 1 riser") : cevir("{0} findings awaiting planning on 1 riser", bulgu);
}

/** Hidrotest sertifikası aranan hat sayısı (ana boruda basınç testi yok) ve NDT'nin sayıldığı hat sayısı. */
export const HIDROTEST_HAT = 4;
export const NDT_HAT = 5;
/** Katman hücresi: "4/4", "3/5". */
export const oranMetni = (n: number, toplam: number): string => `${n}/${toplam}`;

/**
 * Riserin eksik dokümanları (Assembly katmanının Status hücresi; hepsi tamamsa boş, hücre "Assembly report pending" yazar): "Coating report missing", "Hydrotest 3/4" (dört hat: Choke, Kill, Booster, Hydraulic).
 * D-074 Faz 5 · PR 24 (B15): boya raporu yüklü ama hiçbiri final değilse sebep "Coating: no final (N reports)" olarak görünür (sayım kuralı aynı).
 */
export function eksikBelgeler(r: Pick<PanoRiser, "boyaRaporu" | "hidrotest" | "boyaFinalsiz">): string[] {
  const eksik: string[] = [];
  if (!r.boyaRaporu) {
    const n = r.boyaFinalsiz ?? 0;
    // D-074 Faz 5 · PR 24 (B15): rapor var, final yok → sebep görünür.
    eksik.push(n === 0 ? cevir("Coating report missing") : n === 1 ? cevir("Coating: no final (1 report)") : cevir("Coating: no final ({0} reports)", n));
  }
  if (r.hidrotest < HIDROTEST_HAT) eksik.push(cevir("Hydrotest {0}/{1}", r.hidrotest, HIDROTEST_HAT));
  return eksik;
}

/**
 * Finishing katmanının satırı: Finishing'deki hat sayısı ("3/5 lines in Finishing") ve Finishing'e gelmemiş hatların adı ile durumu
 * ("Choke · awaiting planning", "Kill · in repair", "Main · inspection open"; hat adı çevrilmez). Durum hattın tamir durumundan gelir:
 * açık işi varsa "in repair", bulgusu planlama bekliyorsa "awaiting planning", ikisi de yoksa Inspection verification tarafı açıktır.
 * Beş hattı Finishing'de olan riser bu katmanda yalnız beş hat dışı bir komponentteki açık işi yüzünden durur; o zaman tek etiket
 * "another component in repair" yazar (hangi komponent olduğu Repair katmanında görünür).
 */
export function finishingSatiri(r: Pick<PanoRiser, "hatlar">): { oran: string; eksikler: { metin: string; ton: "info" | "warn" | "neutral" }[] } {
  const gelen = r.hatlar.filter((h) => h.finishingde).length;
  const eksikler = r.hatlar
    .filter((h) => !h.finishingde)
    .map((h): { metin: string; ton: "info" | "warn" | "neutral" } =>
      h.tamir === "suruyor" ? { metin: `${h.hat} · ${cevir("in repair")}`, ton: "info" } : h.tamir === "plan_bekliyor" ? { metin: `${h.hat} · ${cevir("awaiting planning")}`, ton: "warn" } : { metin: `${h.hat} · ${cevir("inspection open")}`, ton: "neutral" },
    );
  return { oran: cevir("{0}/{1} lines in Finishing", gelen, r.hatlar.length), eksikler: eksikler.length ? eksikler : [{ metin: cevir("another component in repair"), ton: "info" }] };
}

/**
 * Katmanın en altındaki tek satırlık not; koşulu sağlanmıyorsa "". Girdi batchListesi'dir (seçili kutucuğun listesi DEĞİL): notun
 * konusu o listenin dışındaki riserlerdir (atlananlar, rework gerekmeyenler); Repair'in notu kutucuğun notuyla aynı toplamı
 * yazar. Disposition Report, Finishing, Assembly ve Done katmanının notu yoktur.
 */
export function katmanNotu(k: PanoKutucugu, batchListesi: readonly PanoRiser[], bagsizMrb: number): string {
  switch (k) {
    case "mrb": return bagsizMrbNotu(bagsizMrb);
    case "teslim": return atlananNotu(batchListesi, "teslim");
    case "sokum": return atlananNotu(batchListesi, "sokum");
    case "rework": return reworkGerekmeyenNotu(batchListesi);
    case "tamir": return bulguNotu(batchListesi);
    case "dispo":
    case "finishing":
    case "montaj":
    case "tamam": return "";
  }
}

/**
 * Katmandaki hat karesi: yeşil = hat Finishing'de. Hat adı çevrilmez. Adı bilerek `hatKaresi` değil: o ad lib/riser-belgeleri.ts'te
 * başka ölçüyle (Finishing satırının hat durum kodu) dışa açık.
 */
export function panoHatKaresi(h: Pick<PanoRiser["hatlar"][number], "hat" | "finishingde">): { sinif: string; ipucu: string } {
  return h.finishingde ? { sinif: "bg-ok", ipucu: cevir("{0} · in Finishing", h.hat) } : { sinif: "bg-surface-3", ipucu: h.hat };
}

/* ---------------------------------------------------------------- Repair satırı ---------------------------------------------------------------- */

/** Yüzde = geçilen rota adımı ÷ toplam rota adımı (0–100). Toplam 0 ise 0 (rotası kurulmamış iş; sıfıra bölünmez). */
export function isYuzdesi(s: Pick<PanoIsSatiri, "gecenAdim" | "toplamAdim">): number {
  if (s.toplamAdim <= 0) return 0;
  return Math.min(100, Math.max(0, Math.round((s.gecenAdim / s.toplamAdim) * 100)));
}

/**
 * Yüzdenin yanındaki soluk sayaç: "step 4 of 10" (k = geçilen + 1, toplamı aşmaz); biten işte "10 of 10 steps"; malzeme bekleyen
 * işte ve toplam 0 iken boş.
 */
export function adimSayaci(s: Pick<PanoIsSatiri, "durum" | "gecenAdim" | "toplamAdim">): string {
  if (s.toplamAdim <= 0 || s.durum === "malzeme") return "";
  if (s.durum === "bitti") return s.toplamAdim === 1 ? cevir("1 of 1 step") : cevir("{0} of {1} steps", s.toplamAdim, s.toplamAdim);
  return cevir("step {0} of {1}", Math.min(s.gecenAdim + 1, s.toplamAdim), s.toplamAdim);
}

/** Çubuğun ipucu: "7 of 10 route steps passed". */
export function cubukIpucu(s: Pick<PanoIsSatiri, "gecenAdim" | "toplamAdim">): string {
  return s.toplamAdim === 1 ? cevir("{0} of 1 route step passed", s.gecenAdim) : cevir("{0} of {1} route steps passed", s.gecenAdim, s.toplamAdim);
}

/**
 * Satırın başlığı. Riser numarası ayrı çizilir (açık işte düğme); `ust` onun ardından gelen "Choke · ST-04", `isAdi` soluk iş adı,
 * `ipucu` kesilen metnin tamamı ("R015 · Choke · ST-04 · Connector/flange Replacement"). Hat, ST kodu ve iş adı çevrilmez.
 */
export function isBasligi(s: Pick<PanoIsSatiri, "rn" | "hat" | "poz" | "is">): { ust: string; isAdi: string; ipucu: string } {
  const ust = [s.hat, s.poz].filter(Boolean).join(" · ");
  return { ust, isAdi: s.is, ipucu: [s.rn, ust, s.is].filter(Boolean).join(" · ") };
}

/**
 * Satırın son hücresi ("Last passed step ✔ - current step"). Bütün dallar buradadır; ekran yalnız çizer.
 * `sol.tur`: "gecti" → ad + yeşil ✔ · "kayit_yok" → ad + soluk "no record" ("No record available" ile geçilmiş adım) · "yok" →
 *   soluk "—" (hiç adım geçilmemiş; ad boş).
 * `sag`: şu anki adım; malzeme bekleyen işte "waiting for material"; biten işte null (çizilmez); sürüyor ama adı yoksa soluk "—".
 * `rozet`: açık işte "waiting" (sarı), biten işte "route completed" (yeşil).
 * `ipucu`: kesilen metnin tamamı: "Cutting ✔ - Bevelcut · waiting" · "NDT: DPI (post-test) ✔ - route completed".
 * Adım adları kayıttaki gibidir, çevrilmez.
 */
export interface AdimHucresi {
  sol: { ad: string; tur: "gecti" | "kayit_yok" | "yok" };
  sag: { metin: string; soluk: boolean } | null;
  rozet: { metin: string; ton: "warn" | "ok" };
  ipucu: string;
}
export function adimHucresi(s: Pick<PanoIsSatiri, "durum" | "sonGecen" | "simdiki">): AdimHucresi {
  const sol: AdimHucresi["sol"] = s.sonGecen ? { ad: s.sonGecen.ad, tur: s.sonGecen.kayitYok ? "kayit_yok" : "gecti" } : { ad: "", tur: "yok" };
  const solMetni = sol.tur === "yok" ? "—" : sol.tur === "gecti" ? `${sol.ad} ✔` : `${sol.ad} ${cevir("no record")}`;
  if (s.durum === "bitti") {
    const rozet: AdimHucresi["rozet"] = { metin: cevir("route completed"), ton: "ok" };
    return { sol, sag: null, rozet, ipucu: `${solMetni} - ${rozet.metin}` };
  }
  const sag = s.durum === "malzeme" ? { metin: cevir("waiting for material"), soluk: false } : s.simdiki ? { metin: s.simdiki, soluk: false } : { metin: "—", soluk: true };
  const rozet: AdimHucresi["rozet"] = { metin: cevir("waiting"), ton: "warn" };
  return { sol, sag, rozet, ipucu: `${solMetni} - ${sag.metin} · ${rozet.metin}` };
}

/* ---------------------------------------------------------------- Repair katmanının satırları ---------------------------------------------------------------- */

/**
 * Repair katmanının bir satırı: açık tamir işi ("is") ya da tamir planlaması bekleyen komponent ("plan": komponentte açık iş yok,
 * bulgusu Inspection verification'da tamir planlamasına gönderilmeyi bekliyor; `komponent` hattın ya da beş hat dışındaki
 * komponentin adı, `bulgu` o komponentin bekleyen bulgu sayısı).
 */
export type TamirSatiri = { tur: "is"; is: PanoIsSatiri } | { tur: "plan"; rn: string; komponent: string; bulgu: number };

/**
 * Katmanın ana satırları. Girdi batchListesi. Sıra: riser (girdinin sırası), sonra beş hat (riserin `hatlar` sırası = SET_HATLARI),
 * sonra beş hat dışındaki komponentler (riserin `digerKomponentler` sırası = ad sırası). Komponentte açık iş varsa iş başına bir
 * satır (yanıttaki sırayla); yoksa ve komponent "plan_bekliyor" ise tek "plan" satırı. İş, sunucunun söylediği komponentin altına
 * girer (`PanoIsSatiri.komponent`), adına bakılmaz: eski adla açılmış "C/K 1" işi Choke'un altında, kendi adıyla yazılır.
 *
 * Sayı tutarlılığı: buradaki farklı (riser, komponent) çifti sayısı Repair kutucuğunun ilk sayısına eşittir (`tamirKomponentSayisi`);
 * bir komponentin iki açık işi iki satırdır ama bir komponent sayılır. Sunucu her açık işin komponentini riserin "suruyor" duran bir
 * hattı ya da öteki komponenti olarak gönderir; bu yüzden hiçbir açık iş satırsız kalmaz.
 */
export function tamirSatirlari(isler: readonly PanoIsSatiri[], batchListesi: readonly PanoRiser[]): TamirSatiri[] {
  const acik = acikIsler(isler, batchListesi);
  const satirlar: TamirSatiri[] = [];
  for (const r of batchListesi) {
    const riserIsleri = acik.filter((s) => s.rn === r.rn);
    const komponentler = [...r.hatlar.map((h) => ({ ad: h.hat as string, tamir: h.tamir, bekleyenBulgu: h.bekleyenBulgu })), ...r.digerKomponentler];
    for (const k of komponentler) {
      const isleri = riserIsleri.filter((s) => s.komponent === k.ad);
      if (isleri.length) for (const is of isleri) satirlar.push({ tur: "is", is });
      else if (k.tamir === "plan_bekliyor") satirlar.push({ tur: "plan", rn: r.rn, komponent: k.ad, bulgu: k.bekleyenBulgu });
    }
  }
  return satirlar;
}

/**
 * Planlama bekleyen komponentin satırı: başlığın riser numarasından sonraki kısmı (hattın ya da komponentin adı; çevrilmez),
 * "awaiting planning" rozeti ("waiting" rozetiyle aynı görünüş), soluk bulgu sayısı ve kesilen metinlerin tamamı. Sayı sözcükle
 * birleştirilir ("{0} findings" diye kısa kalıp açılmaz; dosya başındaki nota bakın).
 */
export function planSatiri(s: { rn: string; komponent: string; bulgu: number }): { ust: string; rozet: string; bulgu: string; ipucu: string; baslikIpucu: string } {
  const rozet = cevir("awaiting planning");
  const bulgu = s.bulgu === 1 ? `1 ${cevir("finding")}` : `${s.bulgu} ${cevir("findings")}`;
  return { ust: s.komponent, rozet, bulgu, ipucu: `— · ${rozet} · ${bulgu}`, baslikIpucu: `${s.rn} · ${s.komponent}` };
}

const riserKumesi = (liste: readonly PanoRiser[]): Set<string> => new Set(liste.map((r) => r.rn));
/** Girdi batchListesi: o riserlerin bitmemiş iş satırları, yanıttaki sırayla (riser, hat, açılış). Katman bunları `tamirSatirlari` ile komponentlerin altına dizer. */
export function acikIsler(isler: readonly PanoIsSatiri[], batchListesi: readonly PanoRiser[]): PanoIsSatiri[] {
  const rnler = riserKumesi(batchListesi);
  return isler.filter((s) => s.durum !== "bitti" && rnler.has(s.rn));
}
/** Girdi batchListesi: o riserlerin biten iş satırları ("Completed (n)"); açık işi kalmamış riserinkiler dahil. */
export function bitenIsler(isler: readonly PanoIsSatiri[], batchListesi: readonly PanoRiser[]): PanoIsSatiri[] {
  const rnler = riserKumesi(batchListesi);
  return isler.filter((s) => s.durum === "bitti" && rnler.has(s.rn));
}
/** Kapalı gelen grubun başlığı: "Completed (20)". */
export const bitenBasligi = (n: number): string => cevir("Completed ({0})", n);
